import React from "react";
import classNames from "classnames";
import { Legend, Label, BarChart, CartesianGrid, XAxis, YAxis, Bar } from "recharts";

import DynamicChartArea from "../../components/dynamic_chart_area";
import Layout from "../../components/layout";
import DemoButton from "../../components/demo_button";
import DotDivider from "../../components/dot_divider";
import List from "../../components/list";

import styles from "./program_rankings_2018.module.scss";
import colors from "../../styles/colors.module.scss";
import box from "../../styles/box.module.scss";

const ProgramRankings2018 = () => (
  <Layout pageTitle={"Program Rankings 2018"}>
    <div className={classNames("edo-container", colors.battleshipGray)}>
      <div className={classNames("edo-h1 uk-text-center uk-margin-top")}>
        Top Programs by Search Engagement Rate in 2018
      </div>

      <div className={classNames("uk-margin-medium-top", styles.header)}>
        This year-end summary highlights the programs that indexed highest in 2018 on <b>Search Engagement Rate (SER)</b>, EDO’s proprietary TV ad effectiveness metric.*<br /><br/>
        These three 2018 year-end rankers focus on the top performing TV programs across primetime broadcast, primetime cable, and live sports.
      </div>
      <DotDivider />
    </div>

    <div className={classNames("edo-container")}>
      <div className={classNames("edo-h3 uk-text-center", colors.turquoiseBlue)}>
        BROADCAST AND CABLE TOP 10
      </div>
    </div>

    <div className="edo-container">
      <div className={classNames("uk-margin-top uk-margin-bottom")}>
        <List className={styles.rankerList}>
          <List.Item>
            <List.Content>
              <List.Description className={styles.description}>
                While reality and game shows dominate the top 10 lists, a number of dramas also performed very well for advertisers.
              </List.Description>
            </List.Content>
          </List.Item>
          <List.Item>
            <List.Content>
              <List.Description className={styles.description}>
                <b><i>This is Us</i></b> (NBC) and <b><i>American Horror Story: Apocalypse</i></b> (FX) proved to be the most engaging scripted programs on TV, driving significantly higher SER for their advertisers than average entertainment programming.
              </List.Description>
            </List.Content>
          </List.Item>
          <List.Item>
            <List.Content>
              <List.Description className={styles.description}>
                <b><i>Home Town</i></b> on HGTV delivered the highest average SER across all regularly-scheduled cable TV programs.
              </List.Description>
            </List.Content>
          </List.Item>
        </List>
      </div>

      <div className={styles.broadcast}>
        <div className={classNames(colors.turquoiseBlue, "edo-h4", "uk-text-center", "uk-margin-top")}>Top 10 Broadcast Programs 2018</div>
        <DynamicChartArea mapSizeToStyle={({width}) => ({
          width: "100%",
          height: width / 1.618 > 550 ? width / 1.618 : 550,
        })}>
          <BarChart barCategoryGap="25%" layout="vertical" margin={{ top: 20, right: 20, left: 40, bottom: 40 }} data={[
              {name: "This is Us (NBC)", purple: 145.83},
              {name: "The Proposal (ABC)", green: 136.87},
              {name: "Ellen's Game of Games (NBC)", green: 134.49},
              {name: "Beat Shazam (FOX)", green: 132.33},
              {name: "Young Sheldon (CBS)", teal: 131.65},
              {name: "Shark Tank (ABC)", green: 130.22},
              {name: "The Four: Battle for Stardom (FOX)", green: 128.46},
              {name: "The Wall (NBC)", green: 128.28},
              {name: "The Good Doctor (ABC)", purple: 128.12},
              {name: "Love Connection (FOX)", green: 127.16}
            ]}>
            <CartesianGrid horizontal={false} strokeDasharray="3 3" />
            <YAxis type="category" dataKey="name" width={150}/>
            <XAxis allowDataOverflow={true} domain={[100, 150]} type="number" scale="linear" ticks={[100,105,110,115,120,125,130,135,140,145,150]}>
              <Label value="Average Search Engagement (SER Index)**" offset={0} position="bottom" />
            </XAxis>
            <Bar stackId="broadcast" name="Drama" dataKey="purple" fill="#976cae" />
            <Bar stackId="broadcast" name="Reality / Game Show" dataKey="green" fill="#58bc6e" />
            <Bar stackId="broadcast" name="Sitcom" dataKey="teal" fill="#00b9c6" />
            <Legend wrapperStyle={{bottom: 15}}/>
          </BarChart>
        </DynamicChartArea>

        <div className="edo-container">
          ** Indexed so that 100 represents the SER Index of the average original broadcast program in 2018.
        </div>
        <br />
        <div className="edo-container">
          <b>To be read:</b> <i>Someone who viewed an ad airing during <b>This Is Us</b> on NBC was 46% more likely to search online for the
          brand/product being advertised in the minutes immediately following the ad airing compared to someone who viewed an ad airing during the typical primetime broadcast program.</i>
        </div>
        <DotDivider classes="edo-container"/>
      </div>

      <div className={styles.cable}>
        <div className={classNames(colors.turquoiseBlue, "edo-h4", "uk-text-center", "uk-margin-top")}>Top 10 Cable Programs 2018</div>
        <DynamicChartArea mapSizeToStyle={({width}) => ({
          width: "100%",
          height: width / 1.618 > 550 ? width / 1.618 : 550,
        })}>
          <BarChart barCategoryGap="25%" layout="vertical" margin={{ top: 20, right: 20, left: 40, bottom: 40 }} data={[
              {name: "Home Town (HGTV)", green: 192.73},
              {name: "American Horror Story: Apocalypse (FX)", purple: 170.76},
              {name: "Southern Charm (BRAVO)", green: 159.88},
              {name: "Below Deck (BRAVO)", green: 158.27},
              {name: "Spring Baking Championship (FOOD)", green: 157.52},
              {name: "Naked and Afraid XL (DISCOVERY)", green: 155.19},
              {name: "Lakefront Bargain Hunt (HGTV)", green: 153.39},
              {name: "Vanderpump Rules (BRAVO)", green: 152.24},
              {name: "Yellowstone (PARAMOUNT)", purple: 150.78},
              {name: "The Real Housewives of New Jersey (BRAVO)", green: 150.77}
            ]}>
            <CartesianGrid horizontal={false} strokeDasharray="3 3" />
            <YAxis type="category" dataKey="name" width={150}/>
            <XAxis allowDataOverflow={true} domain={[100, 220]} type="number" scale="linear" ticks={[100,120,140,160,180,200,220]}>
              <Label value="Average Search Engagement (SER Index)**" offset={0} position="bottom" />
            </XAxis>
            <Bar stackId="cable" name="Drama" dataKey="purple" fill="#976cae" />
            <Bar stackId="cable" name="Reality / Game Show" dataKey="green" fill="#58bc6e" />
            <Legend wrapperStyle={{bottom: 15}}/>
          </BarChart>
        </DynamicChartArea>
        <div className="edo-container">
          ** Indexed so that 100 represents the SER Index of the average original broadcast program in 2018.
        </div>
        <DotDivider classes="edo-container"/>
      </div>
    </div>

    <div className={classNames("edo-container")}>
      <div className={classNames("edo-h3 uk-text-center", colors.turquoiseBlue)}>
        LIVE SPORTS TOP 10
      </div>
    </div>
    <div className="edo-container">
      <div className={classNames("uk-margin-top uk-margin-bottom")}>
        <List className={styles.rankerList}>
          <List.Item>
            <List.Content>
              <List.Description className={styles.description}>
                Consistent with previous years, the <b><i>Super Bowl</i></b> was not only the most watched program/event on television in 2018, but it was also the most engaging as measured by SER Index.
              </List.Description>
            </List.Content>
          </List.Item>
          <List.Item>
            <List.Content>
              <List.Description className={styles.description}>
                The top of this list is dominated by playoff/championship sports programming.
              </List.Description>
            </List.Content>
          </List.Item>
          <List.Item>
            <List.Content>
              <List.Description className={styles.description}>
                Overall, <b><i>Monday Night Football</i></b> was the most engaging live sport among all programs outside of playoff/championship sports programming.
              </List.Description>
            </List.Content>
          </List.Item>
        </List>
      </div>
      <div className={styles.sports}>
        <div className={classNames(colors.turquoiseBlue, "edo-h4", "uk-text-center", "uk-margin-top")}>Top 10 Sports Programs 2018</div>
        <DynamicChartArea mapSizeToStyle={({width}) => ({
          width: "100%",
          height: width / 1.618 > 550 ? width / 1.618 : 550,
        })}>
          <BarChart barCategoryGap="25%" layout="vertical" margin={{ top: 20, right: 20, left: 40, bottom: 40 }} data={[
              { name: "NFL Super Bowl (NBC)", orange: 302},
              { name: "NCAAF Championship Game (ESPN)", orange: 240},
              { name: "NBA Finals (ABC)", orange: 230},
              { name: "NFL Monday Night Football (ESPN)", orange: 187},
              { name: "NFL Playoffs (Multiple)", orange: 166},
              { name: "NCAAF New Year's Six Bowls Game (ESPN)", orange: 159},
              { name: "NBA Playoffs (Multiple)", orange: 156},
              { name: "NHL Stanley Cup Finals (NBC)", orange: 155},
              { name: "NFL Sunday Night Football (NBC)", orange: 152},
              { name: "MLB World Series (Fox)", orange: 150},
            ]}>
            <CartesianGrid horizontal={false} strokeDasharray="3 3" />
            <YAxis type="category" dataKey="name" width={150}/>
            <XAxis allowDataOverflow={true} domain={[100, 350]} type="number" scale="linear" ticks={[100,150, 200, 250, 300, 350]}>
              <Label value="Average Search Engagement (SER Index)**" offset={0} position="bottom" />
            </XAxis>
            <Bar stackId="broadcast" name="Sports" dataKey="orange" fill="#f58220" />
          </BarChart>
        </DynamicChartArea>

        <div className="edo-container">
          ** Indexed so that 100 represents the SER Index of the average live event program in 2018.
        </div>

        <DotDivider classes="edo-container"/>

        <div className={classNames("edo-container flex-col flex-align-center", box.marginBottom50)}>
          <div className={classNames(colors.backgroundDarkCream, box.padding20)}>
            * <b>SER</b> measures the effectiveness of a TV ad airing by comparing the relative likelihood that an online action was
            performed in the minutes immediately following a TV ad airing on a per-person, per-second basis. SER is
            typically presented as SER Index, whereby SER is indexed to an explicit and relevant benchmark.  SER controls
            for audience size (impressions), as well as ad duration.
          </div>
        </div>
        <DotDivider classes="edo-container"/>
      </div>
    </div>

    <div className={classNames("edo-container flex-col flex-align-center", box.marginBottom50)}>
      <div className={classNames("edo-h3", "uk-text-center")}>
        Want to discuss? Contact us.
      </div>
      <div className={classNames(styles.button, "uk-flex-row uk-flex-around uk-margin-large-top uk-margin-large-bottom")}>
        <DemoButton label="CONTACT US" gotoLink="/contact_us" buttonSize="large" />
      </div>
      <div className={classNames("edo-h3", "uk-text-center")}>
        An EDO team member will respond to you shortly.
      </div>
    </div>

  </Layout>
);

export default ProgramRankings2018;
